import { useEffect, useRef } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';

/**
 * A dialog with a body — a list to choose from, a small form — rather than the
 * one sentence `Confirm` carries.
 *
 * The Website tab needs three: which template to start from, which version to
 * restore, and what a new questionnaire link should be. Each is a choice made
 * *before* anything is written, so it is not a `Confirm`; where the choice then
 * writes something consequential, a `Confirm` follows it in words.
 *
 * The accessibility is `Confirm`'s, restated rather than shared because
 * `bits.tsx` is common ground in this phase: Esc closes (unless busy), Tab is
 * trapped inside, focus starts on the first control and goes back to whatever
 * opened the dialog when it closes.
 */
export function SiteDialog({
  title,
  onClose,
  busy = false,
  wide = false,
  children,
}: {
  title: string;
  onClose: () => void;
  busy?: boolean;
  wide?: boolean;
  children: ReactNode;
}): ReactNode {
  const panel = useRef<HTMLDivElement>(null);
  const latest = useRef({ busy, onClose });
  latest.current = { busy, onClose };

  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    focusables(panel.current)[0]?.focus();
    return () => {
      if (opener !== null && opener.isConnected) opener.focus();
    };
  }, []);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      if (!latest.current.busy) latest.current.onClose();
      return;
    }
    if (e.key !== 'Tab') return;
    const all = focusables(panel.current);
    const first = all[0];
    const last = all[all.length - 1];
    if (first === undefined || last === undefined) {
      e.preventDefault();
      return;
    }
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  return (
    <div className="modal">
      <div
        ref={panel}
        className={`modal__panel site-dialog${wide ? ' site-dialog--wide' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onKeyDown={onKeyDown}
      >
        <h3>{title}</h3>
        {children}
      </div>
    </div>
  );
}

function focusables(root: HTMLElement | null): HTMLElement[] {
  if (root === null) return [];
  return [
    ...root.querySelectorAll<HTMLElement>(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    ),
  ];
}
